"use client";

import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getTicket,
  Ticket,
  TicketRestoreTimeoutError,
} from "@/services/ticketService";
import { logger } from "@/utils/logger";

export interface TicketQueryRestoreOptions {
  restoreAttempt?: number;
  enabled?: boolean;
}

export function createTicketQueryKey(
  ticketId: string | null,
  identityKey: string | null,
  restoreAttempt: number
) {
  // Keep ticketId second so existing prefix invalidations such as
  // ["ticket", ticketId] continue to cover every identity and restore attempt.
  return [
    "ticket",
    ticketId,
    identityKey ?? "signed-out",
    `restore-attempt:${restoreAttempt}`,
  ] as const;
}

/**
 * Hook to fetch ticket data using React Query
 * Replaces manual fetchWithAuthRetry calls with React Query's caching and error handling
 */
export function useTicketQuery(
  ticketId: string | null,
  identityKey: string | null = null,
  { restoreAttempt = 0, enabled = true }: TicketQueryRestoreOptions = {}
) {
  const queryClient = useQueryClient();
  const queryKey = createTicketQueryKey(ticketId, identityKey, restoreAttempt);
  const query = useQuery<Ticket, Error>({
    queryKey,
    queryFn: async ({ signal }) => {
      if (!ticketId) {
        throw new Error("Ticket ID is required");
      }

      logger.debug("Fetching ticket via React Query", {
        component: "useTicketQuery",
        ticketId,
      });

      return getTicket(ticketId, { signal });
    },
    enabled: !!ticketId && enabled,
    staleTime: 1000 * 60 * 5, // Consider data stale after 5 minutes
    gcTime: 1000 * 60 * 10, // Keep in cache for 10 minutes
    retry: (failureCount, error) => {
      // Don't retry on 404 or 403 errors
      if (error instanceof Error) {
        if (error instanceof TicketRestoreTimeoutError) {
          return false;
        }
        if (
          error.message.includes("not found") ||
          error.message.includes("Access denied")
        ) {
          return false;
        }
      }
      // Retry up to 2 times for other errors
      return failureCount < 2;
    },
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 10000),
  });

  const cancelTicketQuery = useCallback(
    () =>
      queryClient.cancelQueries({
        queryKey: createTicketQueryKey(ticketId, identityKey, restoreAttempt),
        exact: true,
      }),
    [identityKey, queryClient, restoreAttempt, ticketId]
  );

  return {
    ...query,
    cancelTicketQuery,
    restoreAttempt,
  };
}
